import {
  videoPlaybackStateLabel,
  type VideoChannelIdentity,
  type VideoChannelVerificationView,
  type VideoVerificationView,
} from "@shared/videoVerification";
import React, { useEffect, useState } from "react";

interface VideoVerificationPanelProps {
  asOf: string | null;
  channelId: VideoChannelIdentity;
  status: "loading" | "available" | "unavailable";
  view: VideoVerificationView | null;
  onApplyBoundary: (boundary: string | null) => void;
}

function formatUtc(value: string | null | undefined): string | null {
  if (!value) return null;
  const time = new Date(value);
  return Number.isFinite(time.getTime()) ? `${time.toISOString().replace("T", " ").replace(/Z$/, " UTC")}` : null;
}

function normalizeBoundary(value: string): string | null {
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,3})?Z$/.test(trimmed)) return null;
  const wholeSecond = trimmed.slice(0, 19);
  const wholeSecondDate = new Date(`${wholeSecond}Z`);
  if (!Number.isFinite(wholeSecondDate.getTime()) || wholeSecondDate.toISOString().slice(0, 19) !== wholeSecond) return null;
  const parsed = new Date(trimmed);
  return Number.isFinite(parsed.getTime()) ? parsed.toISOString() : null;
}

function PlaybackDetails({ record }: { record: VideoChannelVerificationView }) {
  const playback = record.playback;
  const fields: Array<[string, string | null]> = [
    ["Identity captured", formatUtc(record.identityCapturedAt)],
    ["Observation started", formatUtc(playback.observationStartedAt)],
    ["Observation ended", formatUtc(playback.observationEndedAt)],
    ["Evidence retained", formatUtc(playback.retainedAt)],
    ["Check expires", formatUtc(playback.expiresAt)],
    ["Identity source commit", record.identitySourceCommit],
    ["Playback source commit", playback.sourceCommit],
    ["Policy version", playback.policyVersion],
    ["Receipt", playback.receiptId],
  ].filter((entry): entry is [string, string] => Boolean(entry[1]));
  const observationEndedAt = formatUtc(playback.observationEndedAt);

  return (
    <article className="video-verification-card" data-channel-id={record.channelId}>
      <div className="video-verification-card-head">
        <strong>{record.channel.name}</strong>
        <span>{videoPlaybackStateLabel(playback.state)}</span>
      </div>
      <p className="video-verification-identity">{record.channelId}</p>
      <p>Observation ended: {observationEndedAt ?? "not available at this boundary"}</p>
      {playback.expired && playback.observedState && (
        <p>Last observed result: {videoPlaybackStateLabel(playback.observedState)}. Its daily check has expired.</p>
      )}
      {playback.mediaTimeAdvanceSeconds !== null && (
        <p>Media-time advance: {playback.mediaTimeAdvanceSeconds} seconds</p>
      )}
      {playback.terminalError && <p>Terminal player error: {playback.terminalError}</p>}
      <p className="video-verification-scope">
        Scope: {playback.applicationOrigin ?? "origin unknown"}; {playback.browserName ?? "browser unknown"}
        {playback.browserVersion ? ` ${playback.browserVersion}` : ""}; {playback.observerRegion ?? "region unknown"}.
      </p>
      <details className="video-verification-details">
        <summary>Evidence details</summary>
        <dl>
          {playback.observationIdentity && <div><dt>Playback check identity</dt><dd>{playback.observationIdentity.name} · {playback.observationIdentity.slug} · {playback.observationIdentity.channelId}</dd></div>}
          {playback.observationIdentity && <div><dt>Identity capture</dt><dd>{playback.observationIdentity.captureId} · captured {formatUtc(playback.observationIdentity.capturedAt)}</dd></div>}
          {playback.observationIdentity && <div><dt>Identity capture digest</dt><dd><code>{playback.observationIdentity.captureDigest}</code></dd></div>}
          {fields.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}
          {playback.evidenceDigests.map((digest, index) => <div key={`${digest}-${index}`}><dt>Evidence digest</dt><dd><code>{digest}</code></dd></div>)}
        </dl>
      </details>
    </article>
  );
}

export default function VideoVerificationPanel({
  asOf,
  channelId,
  status,
  view,
  onApplyBoundary,
}: VideoVerificationPanelProps) {
  const [draft, setDraft] = useState(asOf ?? "");
  const [inputError, setInputError] = useState("");

  useEffect(() => setDraft(asOf ?? ""), [asOf]);

  const historical = asOf !== null;
  const selected = view?.channels.find((record) => record.channelId === channelId);
  const records = selected ? [selected] : [];

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!draft.trim()) {
      setInputError("");
      onApplyBoundary(null);
      return;
    }
    const boundary = normalizeBoundary(draft);
    if (!boundary) {
      setInputError("Enter a valid UTC instant, for example 2026-10-12T12:00:00Z.");
      return;
    }
    setInputError("");
    onApplyBoundary(boundary);
  }

  return (
    <section className={`video-verification-panel${historical ? " is-historical" : ""}`} aria-labelledby="video-verification-title">
      <div className="video-verification-heading">
        <div>
          <p className="home-kicker">DATED PLAYBACK VERIFICATION</p>
          <h2 id="video-verification-title">{historical ? "What was retained by this time" : "Playback check record"}</h2>
        </div>
        {view && <span className="video-verification-asof">Boundary: {formatUtc(view.asOf)}</span>}
      </div>

      <form className="video-boundary-form" onSubmit={submit}>
        <label htmlFor="video-as-of">Historical UTC boundary (ISO 8601)</label>
        <div>
          <input
            id="video-as-of"
            type="text"
            inputMode="text"
            autoComplete="off"
            spellCheck={false}
            placeholder="2026-10-12T12:00:00Z"
            value={draft}
            onChange={(event) => setDraft(event.currentTarget.value)}
          />
          <button type="submit">{draft.trim() ? "View date" : "Show live"}</button>
        </div>
        {inputError && <p role="alert">{inputError}</p>}
      </form>

      {status === "loading" && <p role="status">Playback state unknown while verification data loads.</p>}
      {status === "unavailable" && <p role="status">Playback state unknown. The verification projection is unavailable.</p>}
      {status === "available" && view && historical && !selected && (
        <p className="video-verification-empty" role="status">
          No frozen identity record for {channelId} was retained by this boundary. Current channel metadata is not used here.
        </p>
      )}
      {status === "available" && view && !historical && records.length === 0 && (
        <p role="status">Playback state unknown. No dated check is available for this channel.</p>
      )}
      {records.length > 0 && <div className="video-verification-records">{records.map((record) => <PlaybackDetails key={record.channelId} record={record} />)}</div>}
      {historical && (
        <p className="video-verification-limit">
          Historical media is not retained. This view shows frozen channel identity and playback checks only; a historical broadcast is not available.
        </p>
      )}
      <p className="video-verification-scope">
        A dated check applies only to its recorded browser, application origin, region, and observation time. It does not establish current playback.
      </p>
    </section>
  );
}
